import React from "react";
import { RefreshCw, Brain, Plus, ChevronDown, ChevronUp, Trash2, Check, Headphones, Radio, Link } from "lucide-react";
import { PodcastEpCard, STATUS_META } from "./PodcastEpCard";

const LANES = [
  { id: "backlog",   label: "Backlog",       icon: <Plus className="w-3.5 h-3.5" /> },
  { id: "draft",     label: "Draft Script",  icon: <Brain className="w-3.5 h-3.5" /> },
  { id: "approved",  label: "Approved",      icon: <Check className="w-3.5 h-3.5" /> },
  { id: "audio",     label: "Audio",         icon: <Headphones className="w-3.5 h-3.5" /> },
  { id: "published", label: "Published",     icon: <Radio className="w-3.5 h-3.5" /> },
];

function weekDisplay() {
  const d = new Date();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return `${mon.toLocaleDateString("en-GB", { month: "short", day: "numeric" })} – ${sun.toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" })}`;
}

export function PodcastKanbanBoard({
  epsByLane, queue, loadingEps,
  queueOpen, onQueueToggle,
  articleSearch, onArticleSearchChange,
  searchResults, searching, runningSelection,
  generatingScript, pollingScript, recordingEpId, publishingEpId, activeEpId,
  elModel,
  dm, c, btnPrimary, btnSecondary, inputCls,
  onRefresh, onGenerateScript, onAutoSelect, onAddToQueue, onRemoveFromQueue,
  onSelectEp, onGenerateAudio, onPublish,
  onSyncSpreaker, syncingSpReaker,
  forceUrl = "", onForceUrlChange, onForceUrlAdd, scrapingForce = false,
  detailPanel,
}) {
  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.muted}`}>Podcast Pipeline</p>
          <p className={`text-sm ${c.text}`}>{weekDisplay()}</p>
        </div>
        <div className="flex items-center gap-2">
          {(pollingScript || recordingEpId || publishingEpId) && (
            <span className={`flex items-center gap-1.5 text-xs ${c.muted}`}>
              <RefreshCw className="w-3 h-3 animate-spin" />
              {recordingEpId ? "Generating audio…" : publishingEpId ? "Publishing…" : "Generating script…"}
            </span>
          )}
          <button onClick={onRefresh} className={btnSecondary}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          {(epsByLane.published || []).length > 0 && (
            <button
              onClick={onSyncSpreaker}
              disabled={syncingSpReaker}
              className={btnSecondary}
              title="Reconcile published episodes with Spreaker — removes episodes deleted on Spreaker"
              data-testid="sync-spreaker-btn"
            >
              {syncingSpReaker
                ? <RefreshCw className="w-3 h-3 animate-spin" />
                : <Radio className="w-3 h-3" />}
              {syncingSpReaker ? "Syncing…" : "Sync Spreaker"}
            </button>
          )}
          <button
            onClick={onGenerateScript}
            disabled={generatingScript || pollingScript}
            className={btnPrimary}
            data-testid="generate-script-btn"
          >
            {generatingScript
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              : <Brain className="w-3.5 h-3.5" />}
            {generatingScript ? "Generating…" : "Generate Script"}
          </button>
        </div>
      </div>

      {/* Kanban lanes */}
      <div className="grid grid-cols-5 gap-3">
        {LANES.map(lane => {
          const cards = lane.id === "backlog" ? [] : (epsByLane[lane.id] || []);
          const isBacklog = lane.id === "backlog";

          return (
            <div key={lane.id} className={`rounded-2xl ${c.lane} p-3 min-h-[220px] space-y-2`}>
              {/* Lane header */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className={c.muted}>{lane.icon}</span>
                  <p className={`text-[11px] font-bold uppercase tracking-wider ${c.muted}`}>
                    {lane.label}
                  </p>
                </div>
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${
                  dm ? "bg-white/10 text-slate-300" : "bg-black/8 text-[#6b7a7d]"
                }`}>
                  {isBacklog ? queue.length : cards.length}
                </span>
              </div>

              {/* Backlog lane content */}
              {isBacklog && (
                <div className="space-y-2">
                  <div className={`rounded-xl border ${c.border} ${c.card} p-3 space-y-2`}>
                    <p className={`text-xs font-semibold ${c.text}`}>
                      {queue.length} article{queue.length !== 1 ? "s" : ""} queued
                    </p>
                    <div className="flex gap-1.5 flex-wrap">
                      <button
                        onClick={onAutoSelect}
                        disabled={runningSelection}
                        className={btnSecondary}
                        data-testid="auto-select-btn"
                      >
                        {runningSelection
                          ? <RefreshCw className="w-3 h-3 animate-spin" />
                          : <Plus className="w-3 h-3" />}
                        Auto-select
                      </button>
                      <button onClick={onQueueToggle} className={btnSecondary}>
                        {queueOpen
                          ? <ChevronUp className="w-3 h-3" />
                          : <ChevronDown className="w-3 h-3" />}
                        {queueOpen ? "Hide" : "Manage"}
                      </button>
                    </div>
                  </div>

                  {queueOpen && (
                    <div className="space-y-1.5">
                      <input
                        type="text"
                        value={articleSearch}
                        onChange={e => onArticleSearchChange(e.target.value)}
                        placeholder="Search articles…"
                        className={inputCls}
                        data-testid="queue-search-input"
                      />
                      {searching && <p className={`text-xs ${c.muted}`}>Searching…</p>}

                      {/* Force-Include URL input */}
                      <div className={`rounded-xl border ${c.border} ${c.card} p-2 space-y-1.5`}>
                        <p className={`text-[9px] font-bold uppercase tracking-widest ${c.muted}`}>
                          <Link className="w-2.5 h-2.5 inline mr-1" />Force URL
                        </p>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={forceUrl}
                            onChange={e => onForceUrlChange(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && !scrapingForce && onForceUrlAdd()}
                            placeholder="https://…"
                            className={inputCls}
                            data-testid="podcast-force-url-input"
                          />
                          <button
                            onClick={onForceUrlAdd}
                            disabled={scrapingForce || !forceUrl.trim()}
                            data-testid="podcast-force-url-btn"
                            className={`${btnPrimary} flex-shrink-0 whitespace-nowrap disabled:opacity-40`}
                          >
                            {scrapingForce
                              ? <RefreshCw className="w-3 h-3 animate-spin" />
                              : <Plus className="w-3 h-3" />}
                            {scrapingForce ? "" : "Add"}
                          </button>
                        </div>
                      </div>

                      {/* Search results */}
                      {searchResults.map(a => (
                        <div key={a.id} className={`rounded-lg px-2 py-1.5 flex items-center gap-1.5 ${c.card} border ${c.border}`}>
                          <p className={`flex-1 text-[10px] truncate ${c.text}`}>{a.title}</p>
                          <button
                            onClick={() => onAddToQueue(a)}
                            className="text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      ))}

                      {/* Queued articles */}
                      {queue.map((a, i) => {
                        const artId = a.article_id || a.id || `item-${i}`;
                        return (
                          <div key={artId} className={`rounded-lg px-2 py-1.5 flex items-center gap-1.5 ${c.card} border ${c.border}`}>
                            <p className={`flex-1 text-[10px] truncate ${c.text}`}>{a.title}</p>
                            <p className={`text-[9px] ${c.muted} flex-shrink-0`}>{a.source}</p>
                            <button
                              onClick={() => onRemoveFromQueue(artId)}
                              className="text-red-400 hover:text-red-300 flex-shrink-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Episode cards */}
              {!isBacklog && (
                loadingEps && cards.length === 0
                  ? <p className={`text-xs ${c.muted} text-center py-4`}>Loading…</p>
                  : cards.length === 0
                    ? <p className={`text-xs ${c.muted} text-center py-6`}>—</p>
                    : cards.map(ep => (
                        <PodcastEpCard
                          key={ep.id}
                          ep={ep}
                          dm={dm}
                          c={c}
                          btnPrimary={btnPrimary}
                          btnSecondary={btnSecondary}
                          isActive={activeEpId === ep.id}
                          recordingEpId={recordingEpId}
                          publishingEpId={publishingEpId}
                          pollingScript={pollingScript}
                          elModel={elModel}
                          onSelect={onSelectEp}
                          onGenerateAudio={onGenerateAudio}
                          onPublish={onPublish}
                        />
                      ))
              )}
            </div>
          );
        })}
      </div>

      {/* Detail panel — shown below kanban when an episode is selected */}
      {detailPanel && (
        <div className="mt-2">{detailPanel}</div>
      )}
    </div>
  );
}

// Re-export STATUS_META for use in PodcastTab
export { STATUS_META };
